import { Bleed, Divider, useModalContext } from '@machobear/design-system'
import * as Yup from 'yup'
import { Children, useState, useEffect } from 'react'
import { Form, Formik, useFormikContext, FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'
import { ObjectShape } from 'yup/lib/object'
import { noop } from 'lodash'
import { useWizardContext } from './wizard-context'
import WizardFooter from './wizard-footer'
type Props<T> = {
  children: React.ReactNode
  initialValues: T
  onSubmit: (values: T, bag: FormikHelpers<T>) => void
  validationSchema?: any
  onBack?: () => void
  submitText?: string
  intitalStep?: number
}
type Child = {
  props: {
    enabled?: boolean
    children?: React.ReactNode
    initialValues?: any
    onSubmit?: (values: any, bag: any) => void
    validationSchema?: Yup.ObjectSchema<ObjectShape>
    backButtonId?: string
    submitButtonId?: string
  }
}

//needs to be wrapped in a wizard Context provider
const Wizard = <T,>({
  children,
  initialValues,
  onSubmit,
  submitText,
  onBack = noop,
  intitalStep = 0
}: Props<T>) => {
  const { t } = useTranslation()
  const [stepNumber, setStepNumber] = useState(intitalStep)
  const [snapshot, setSnapshot] = useState(initialValues)
  const { loading, submitting } = useWizardContext()
  const steps = Children.toArray(children)?.filter(
    (child: { props: { enabled?: boolean; children?: React.ReactNode } }) => {
      return child.props.enabled ?? Boolean(child?.props?.children)
    }
  )
  const step = steps?.[stepNumber] as Child

  const totalSteps = steps.length
  const isLastStep = stepNumber === totalSteps - 1
  const submitLabel = submitText ? submitText : t('common.submit', 'Submit')
  const submitButtonText = isLastStep ? submitLabel : t('common.continue', 'Continue')

  const next = (values: any) => {
    setSnapshot(values)
    setStepNumber(Math.min(stepNumber + 1, totalSteps - 1))
  }

  const previous = (values: any) => {
    setSnapshot(values)
    setStepNumber(Math.max(stepNumber - 1, 0))
    onBack()
  }

  const handleSubmit = async (values: any, helpers: FormikHelpers<any>) => {
    if (step?.props.onSubmit) {
      await step?.props.onSubmit(values, helpers)
    }
    if (isLastStep) {
      return onSubmit(values, helpers)
    } else {
      helpers.setTouched({})
      next(values)
    }
  }

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={step?.props.validationSchema}
    >
      {formik => {
        const disabled = formik.isSubmitting || loading || submitting

        const text =
          formik.isSubmitting || submitting
            ? t('common.submitting', 'Submitting...')
            : loading
            ? t('common.loading', 'Loading...')
            : submitButtonText
        return (
          <Form>
            <>
              <Bleed horizontal={{ mobile: 'gutter', tablet: 'large' }}>
                <Divider />
              </Bleed>
              {step}
              <WizardFooter
                stepNumber={stepNumber}
                disabled={disabled}
                handleBack={() => previous(formik.values)}
                submitLabel={text}
                handleSubmit={formik.handleSubmit}
                submitButtonId={step?.props?.submitButtonId}
                backButtonId={step?.props?.backButtonId}
              />
            </>
          </Form>
        )
      }}
    </Formik>
  )
}

const WizardStep = ({
  children,
  enabled,
  validationSchema,
  initialValues,
  onSubmit,
  backButtonId = '',
  submitButtonId = ''
}: {
  children: React.ReactNode
  enabled: boolean
  validationSchema?: any
  initialValues?: any
  onSubmit?: (values: any, bag: any) => void
  backButtonId?: string
  submitButtonId?: string
}) => {
  const { enableCloseWarning } = useModalContext()
  const { dirty } = useFormikContext()

  useEffect(() => {
    if (dirty) enableCloseWarning()
  }, [dirty, enableCloseWarning])

  if (enabled) return <>{children}</>
  return null
}

export { WizardStep, Wizard }
